import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSuccessionTheme } from './SuccessionThemeProvider';

const DocumentPreview = ({ content, colors, themeColors }) => {
  const { theme, isLightTheme, isDarkTheme, company } = useSuccessionTheme();
  
  // Use passed colors or fallback to theme context
  const actualColors = colors || {
    primary: company?.primary_color || '#5489B6',
    secondary: company?.secondary_color || '#002949',
    accent: company?.accent_color || '#5B5E5A',
    neutral: company?.neutral_color || '#666666',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  };

  const actualThemeColors = themeColors || {
    bgPrimary: isDarkTheme ? '#1f2937' : '#ffffff',
    bgSecondary: isDarkTheme ? '#111827' : '#f9fafb',
    bgTertiary: isDarkTheme ? '#374151' : '#e5e7eb',
    textPrimary: isDarkTheme ? '#f9fafb' : '#111827',
    textSecondary: isDarkTheme ? '#d1d5db' : '#6b7280',
    textMuted: isDarkTheme ? '#9ca3af' : '#9ca3af',
    border: isDarkTheme ? '#374151' : '#e5e7eb',
    shadow: isDarkTheme ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    shadowLg: isDarkTheme ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  };

  const paperStyles = {
    border: `1px solid ${actualThemeColors.border}`,
    borderRadius: '2px',
    padding: '0.75rem',
    maxWidth: '48rem',
    margin: '0 auto',
    backgroundColor: actualThemeColors.bgSecondary,
    backgroundImage: `linear-gradient(${actualThemeColors.bgTertiary} 1px, transparent 1px)`,
    backgroundSize: '100% 28px',
    position: 'relative',
    boxShadow: actualThemeColors.shadow,
    transition: 'background-color 0.3s ease, border-color 0.3s ease'
  };

  const redLineStyles = {
    content: '',
    position: 'absolute',
    left: '30px',
    top: 0,
    bottom: 0,
    width: '1px',
    backgroundColor: 'rgba(255,0,0,0.1)',
    height: '100%'
  };

  const contentStyles = {
    fontFamily: 'Times New Roman, serif',
    color: actualThemeColors.textPrimary,
    lineHeight: '1.5',
    padding: '0.5rem 0.75rem',
    transition: 'color 0.3s ease'
  };

  const headingStyles = {
    fontFamily: 'Times New Roman, serif',
    fontWeight: 'bold',
    color: actualThemeColors.textPrimary,
    transition: 'color 0.3s ease'
  };

  return (
    <div className="p-4">
      <div style={paperStyles}>
        <div style={redLineStyles}></div>
        <div 
          className="prose max-w-none" 
          style={contentStyles}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 style={{...headingStyles, fontSize: '24px', borderBottom: `1px solid ${actualThemeColors.border}`, paddingBottom: '8px', marginBottom: '16px'}} {...props} />,
              h2: ({node, ...props}) => <h2 style={{...headingStyles, fontSize: '20px', marginTop: '24px', marginBottom: '12px'}} {...props} />,
              h3: ({node, ...props}) => <h3 style={{...headingStyles, fontSize: '18px', marginTop: '16px', marginBottom: '8px'}} {...props} />,
              p: ({node, ...props}) => <p style={{fontSize: '16px', marginTop: '8px', marginBottom: '8px', color: actualThemeColors.textPrimary}} {...props} />,
              li: ({node, ...props}) => <li style={{fontSize: '16px', color: actualThemeColors.textPrimary}} {...props} />
            }}
          >
            {content || '# Business Continuity Plan\n\nStart filling out the questionnaire to generate your BCP document.'}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview; 